import { useState, useCallback } from "react";
import {
  address as toAddress,
  appendTransactionMessageInstructions,
  createTransactionMessage,
  getSignatureFromTransaction,
  pipe,
  setTransactionMessageFeePayerSigner,
  setTransactionMessageLifetimeUsingBlockhash,
  signTransactionMessageWithSigners,
} from "@solana/kit";
import type { Address, Instruction } from "@solana/kit";
import {
  ASSOCIATED_TOKEN_PROGRAM_ADDRESS,
  findAssociatedTokenPda,
  getCreateAssociatedTokenIdempotentInstruction,
  getTransferCheckedInstruction,
  TOKEN_PROGRAM_ADDRESS,
} from "@solana-program/token";
import { useBrowserWallet } from "@nullaone/wallet-react";
import { useTranslation } from "react-i18next";
import { solanaService } from "../services/solana";
import { sendSignedTransaction } from "../api/relay";
import { useTransactionStore } from "../store/transaction-store";
import { useToast } from "./useToast";
import { formatSolanaError } from "../lib/solana-errors";
import {
  requestTransactionConfirmation,
  TransactionRejectedError,
} from "@/features/wallet";
import { shortenAddress } from "@/lib/solana/format";
import type { TransactionResult } from "../types/solana";

const I18N = {
  titleSendSol: "wallet.txConfirm.titles.sendSol",
  titleSendToken: "wallet.txConfirm.titles.sendToken",
  labelTo: "wallet.txConfirm.labels.to",
  labelAmount: "wallet.txConfirm.labels.amount",
  labelFrom: "wallet.txConfirm.labels.from",
} as const;

/**
 * Canonical return value when the user cancels at the pre-sign confirmation
 * dialog. Distinguishable from a real failure via `error === "UserRejected"`;
 * callers should branch on this to avoid showing a generic "transaction
 * failed" error state.
 */
const USER_REJECTED_RESULT: TransactionResult = {
  signature: "",
  confirmed: false,
  error: "UserRejected",
  message: "Cancelled by user",
};

// Solana mainnet chain ID (not standard, using 101 for Solana mainnet)
const SOLANA_CHAIN_ID = 101;

/**
 * Hook to send SOL or SPL tokens.
 *
 * Phase 6 slice 4: rewritten on Kit + wallet-react `signer`. The legacy
 * `signTransaction(VersionedTransaction)` shape is gone — Kit-native
 * pipelines compose the connected wallet's `TransactionSigner` directly via
 * `setTransactionMessageFeePayerSigner` and `signTransactionMessageWithSigners`.
 *
 * Token transfers go through @solana-program/token's
 * `getCreateAssociatedTokenIdempotentInstruction` + `getTransferCheckedInstruction`,
 * mirroring the SPL Token program's TransferChecked semantics.
 */
export function useSolanaSendTransaction() {
  const { signer, publicKey } = useBrowserWallet();
  const { addTransaction, updateTransaction } = useTransactionStore();
  const { success, error: showError } = useToast();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const sendSOL = useCallback(
    async (to: string, amount: number): Promise<TransactionResult> => {
      if (!publicKey || !signer) {
        throw new Error("Wallet not connected");
      }

      const fromAddress = publicKey;

      try {
        // Validate address up front so a bad input throws BEFORE we open the
        // confirmation dialog (no point asking the user to confirm an invalid
        // destination).
        const toAddr = toAddress(to);

        // Pre-sign confirmation gate. Runs BEFORE setIsLoading + addTransaction
        // so a user-cancel leaves no "pending" state to clean up. Rejects with
        // `TransactionRejectedError`, narrow-caught below.
        await requestTransactionConfirmation({
          title: t(I18N.titleSendSol),
          details: [
            { label: t(I18N.labelTo), value: shortenAddress(to) },
            { label: t(I18N.labelAmount), value: `${amount.toFixed(4)} SOL` },
            { label: t(I18N.labelFrom), value: shortenAddress(signer.address) },
          ],
        });

        setIsLoading(true);
        const tempHash = `pending-${Date.now()}`;
        addTransaction({
          hash: tempHash,
          type: "token_transfer",
          status: "pending",
          chainId: SOLANA_CHAIN_ID,
          from: fromAddress,
          to,
          value: amount.toString(),
          description: `Send ${amount} SOL to ${to.slice(0, 8)}...${to.slice(-4)}`,
        });

        const result = await solanaService.sendSOL(
          fromAddress,
          toAddr,
          amount,
          signer,
        );

        // Three terminal states surfaced by the relay:
        //   1. signature && confirmed   → on-chain confirmed (real success)
        //   2. signature && !confirmed  → broadcast OK, relay's ~5s confirm
        //      poll elapsed before the cluster reported confirmation. The tx
        //      will land — surfacing this as "failed" is the bug fixed here.
        //   3. no signature             → broadcast itself failed.
        if (result.signature && result.confirmed) {
          updateTransaction(tempHash, {
            hash: result.signature,
            status: "confirmed",
          });
          success("Transaction Successful", {
            description: `Sent ${amount} SOL to ${to.slice(0, 8)}...${to.slice(-8)}`,
          });
        } else if (result.signature) {
          updateTransaction(tempHash, {
            hash: result.signature,
            status: "pending",
          });
          success(t("wallet.transferSubmitted"), {
            description: t("wallet.transferSubmittedToast"),
          });
        } else {
          updateTransaction(tempHash, {
            status: "failed",
            error: result.message,
          });
          showError("Transaction Failed", {
            description: result.message || "Transaction could not be completed",
          });
        }

        return result;
      } catch (error) {
        if (error instanceof TransactionRejectedError) {
          // User cancelled at the pre-sign dialog — silent, no error toast.
          // The dialog has already closed itself; callers branch on
          // `result.error === "UserRejected"`.
          return USER_REJECTED_RESULT;
        }
        const errorMessage = formatSolanaError(error);
        showError("Transaction Error", {
          description: errorMessage,
        });

        return {
          signature: "",
          confirmed: false,
          error: "Unknown",
          message: errorMessage,
        };
      } finally {
        setIsLoading(false);
      }
    },
    [
      publicKey,
      signer,
      addTransaction,
      updateTransaction,
      success,
      showError,
      t,
    ],
  );

  const sendToken = useCallback(
    async (
      to: string,
      mint: string,
      amount: number,
      decimals: number,
      symbol: string,
    ): Promise<TransactionResult> => {
      if (!publicKey || !signer) {
        throw new Error("Wallet not connected");
      }

      const fromAddress: Address = publicKey;

      try {
        // Validate addresses up front so bad input throws BEFORE we open the
        // confirmation dialog.
        const toAddr = toAddress(to);
        const mintAddr = toAddress(mint);
        const rpc = solanaService.getRpc();

        // Pre-sign confirmation gate. See sendSOL above for rationale.
        await requestTransactionConfirmation({
          title: t(I18N.titleSendToken),
          details: [
            { label: t(I18N.labelTo), value: shortenAddress(to) },
            { label: t(I18N.labelAmount), value: `${amount} ${symbol}` },
            { label: t(I18N.labelFrom), value: shortenAddress(signer.address) },
          ],
        });

        setIsLoading(true);
        const tempHash = `pending-${Date.now()}`;
        addTransaction({
          hash: tempHash,
          type: "token_transfer",
          status: "pending",
          chainId: SOLANA_CHAIN_ID,
          from: fromAddress,
          to,
          value: amount.toString(),
          description: `Send ${amount} ${symbol} to ${to.slice(0, 8)}...${to.slice(-4)}`,
        });

        // Resolve associated token addresses for both source and destination.
        const [fromAta] = await findAssociatedTokenPda({
          owner: fromAddress,
          tokenProgram: TOKEN_PROGRAM_ADDRESS,
          mint: mintAddr,
        });
        const [toAta] = await findAssociatedTokenPda({
          owner: toAddr,
          tokenProgram: TOKEN_PROGRAM_ADDRESS,
          mint: mintAddr,
        });

        const instructions: Instruction[] = [
          // Idempotent ATA create on the recipient side — no-ops if already
          // initialized, so we don't need a separate `getAccountInfo` round-trip.
          getCreateAssociatedTokenIdempotentInstruction(
            {
              payer: signer,
              ata: toAta,
              owner: toAddr,
              mint: mintAddr,
              tokenProgram: TOKEN_PROGRAM_ADDRESS,
            },
            { programAddress: ASSOCIATED_TOKEN_PROGRAM_ADDRESS },
          ),
          getTransferCheckedInstruction(
            {
              source: fromAta,
              mint: mintAddr,
              destination: toAta,
              authority: signer,
              amount: BigInt(Math.floor(amount * Math.pow(10, decimals))),
              decimals,
            },
            { programAddress: TOKEN_PROGRAM_ADDRESS },
          ),
        ];

        const { value: latestBlockhash } = await rpc
          .getLatestBlockhash({ commitment: "confirmed" })
          .send();

        const message = pipe(
          createTransactionMessage({ version: 0 }),
          (m) => setTransactionMessageFeePayerSigner(signer, m),
          (m) =>
            setTransactionMessageLifetimeUsingBlockhash(latestBlockhash, m),
          (m) => appendTransactionMessageInstructions(instructions, m),
        );

        const signed = await signTransactionMessageWithSigners(message);
        const relayed = await sendSignedTransaction(signed);
        const signature = getSignatureFromTransaction(signed);

        // SPL transfer always has a signature post-sign (we compute it locally
        // from the signed message), so the no-signature branch from sendSOL
        // doesn't apply here — only the confirmed / pending split matters.
        if (relayed.confirmed) {
          updateTransaction(tempHash, {
            hash: signature,
            status: "confirmed",
          });
          success("Transaction Successful", {
            description: `Sent ${amount} ${symbol} to ${to.slice(0, 8)}...${to.slice(-8)}`,
          });
        } else {
          updateTransaction(tempHash, {
            hash: signature,
            status: "pending",
          });
          success(t("wallet.transferSubmitted"), {
            description: t("wallet.transferSubmittedToast"),
          });
        }

        return { signature, confirmed: relayed.confirmed };
      } catch (error) {
        if (error instanceof TransactionRejectedError) {
          return USER_REJECTED_RESULT;
        }
        const errorMessage = formatSolanaError(error);
        showError("Transaction Error", {
          description: errorMessage,
        });

        return {
          signature: "",
          confirmed: false,
          error: "Unknown",
          message: errorMessage,
        };
      } finally {
        setIsLoading(false);
      }
    },
    [
      publicKey,
      signer,
      addTransaction,
      updateTransaction,
      success,
      showError,
      t,
    ],
  );

  return {
    sendSOL,
    sendToken,
    isLoading,
  };
}
